import { Spinner } from '@sbab-ui/components'
import { useEffect, useReducer, useState } from 'react'
import {
  fetchExampleRate,
  fetchMortgageRates,
  fetchMortgageTerms,
  fetchPersonalLoanTerms,
} from './api/httpHandlers'
import { DEFAULT_PROPERTY_VALUE, DEFAULT_STATE } from './appConfig'
import { appReducer, createInitialState } from './appState'
import CostsCalculator from './components/CostsCalculator'
import ErrorMessage from './components/ErrorMessage'
import MockQueryButton from './components/MockQueryButton'
import MortgageCalculator from './components/MortgageCalculator'
import ObjectSummary from './components/ObjectSummary'
import PersonalLoan from './components/PersonalLoan'
import ResetButton from './components/ResetButton'
import Result from './components/Result'
import { getEssentialDerivedState } from './lib/calculationUtils'
import { MainHeading } from './components/typographyComponents'
import VillaBanner from './components/VillaBanner'

function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [state, dispatch] = useReducer(appReducer, DEFAULT_STATE, createInitialState)
  // This is used for the debounceable values in MortgageCalculator
  // It is moved up to App because it needs to be reset along with the app state
  const [mortgageValues, setMortgageValues] = useState({
    propertyValue: state.propertyValue,
    downPayment: state.downPayment,
  })
  function resetHandler() {
    dispatch({ type: 'reset' })
    const propertyValue = state?.houseObject?.pris ?? DEFAULT_PROPERTY_VALUE
    setMortgageValues({
      propertyValue,
      downPayment: +(propertyValue * 0.15).toFixed(0),
    })
  }

  useEffect(() => {
    async function getAppData() {
      const [mortgageRatesResult, mortgageTermsResult, exampleRateResult, personalLoanTermsResult] =
        await Promise.all([
          fetchMortgageRates(),
          fetchMortgageTerms(),
          fetchExampleRate(),
          fetchPersonalLoanTerms(),
        ])
      if (
        mortgageRatesResult.successful &&
        mortgageTermsResult.successful &&
        mortgageTermsResult.data &&
        exampleRateResult.successful &&
        exampleRateResult.data &&
        personalLoanTermsResult.successful &&
        personalLoanTermsResult.data
      ) {
        dispatch({
          type: 'appDataFetched',
          listInterests: mortgageRatesResult.data,
          mortgageTerms: mortgageTermsResult.data,
          personalLoanTerms: personalLoanTermsResult.data,
          personalLoanExampleRate: +exampleRateResult.data.items[0].rate,
        })
        setIsLoading(false)
      } else {
        setIsLoading(false)
        dispatch({ type: 'error' })
      }
    }
    getAppData()
  }, [])

  const { showPersonalLoan } = getEssentialDerivedState(
    state.downPayment,
    state.propertyValue,
    state.personalLoanIsAdded,
    state.personalLoanAmount,
  )

  // The window.pcaConfig?.villaBanner is feature toggleable
  const villaBannerOn = window.pcaConfig?.villaBanner
  // The window.pcaConfig?.mobileResultReOrder is feature toggleable
  const mobileResultReOrder = window.pcaConfig?.mobileResultReOrder

  function getIsVilla(objekttyp: number | undefined) {
    if (!objekttyp) return false
    if (objekttyp !== 100) return false
    return true
  }
  const isVilla = getIsVilla(state.houseObject?.objekttyp)

  return (
    <div className="mx-auto max-w-[1280px] text-black">
      {isLoading ? (
        <div className="flex h-96 items-center justify-center">
          <Spinner size="large" />
        </div>
      ) : null}
      {state.hasAppError ? <ErrorMessage /> : null}
      {!isLoading && !state.houseObject ? <MockQueryButton /> : null}
      {!isLoading && !state.hasAppError ? (
        <>
          {isVilla && villaBannerOn ? <VillaBanner /> : null}
          <MainHeading>Räkna på vad bostaden kostar</MainHeading>
          <div className="mb-6 grid gap-6 min-[851px]:grid-cols-[1fr_450px]">
            <div className="flex flex-col gap-6 max-w-[calc(100vw-16px)]">
              <div className="flex flex-col">
                <ObjectSummary appState={state} />

                {/* Conditional rendering for mobile view to place Result right after ObjectSummary */}
                <div
                  className={`mt-6 ${mobileResultReOrder ? 'block min-[851px]:hidden' : 'hidden'}`}
                >
                  <Result appState={state} />
                </div>
              </div>

              <MortgageCalculator
                appState={state}
                appDispatch={dispatch}
                mortgageValues={mortgageValues}
                setMortgageValues={setMortgageValues}
              />

              <PersonalLoan
                appState={state}
                appDispatch={dispatch}
                show={showPersonalLoan && !state.hasCalculationError}
              />
              <CostsCalculator appState={state} appDispatch={dispatch} />
              <ResetButton resetHandler={resetHandler} />
            </div>

            {/* This ensures Result is only shown after ObjectSummary on mobile, and retains its position on desktop */}
            <div
              className={`h-full max-w-[calc(100vw-16px)] ${
                mobileResultReOrder ? 'hidden min-[851px]:block' : ''
              }`}
            >
              <Result appState={state} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default App
